import { useState } from "react";
import { Button } from "../../Button/Button";
import { LoadingPlaceholder } from "../../LoadingPlaceholder/LoadingPlaceholder";
import { Modal } from "../../Modal/Modal";
import { Body } from "../../Typography/Body";
import "./ChatReferenceImage.css";

export interface ChatReferenceImageProps {
  imageUrl?: string;
  loading: boolean;
  caption: string;
  altText: string;
  sourceTitle: string;
  /** External source page, opened in a new tab. */
  sourceUrl?: string;
  /** In-app source (e.g. the AMA Guide viewer), used when there is no `sourceUrl`. */
  onSourceClick?: () => void;
  onRetry: () => void;
  onLoadError?: () => void;
  /**
   * Image only, for images that carry their own caption (e.g. a scanned book
   * table): no heading, caption or padding, a larger preview, and a card that
   * hugs the image's width. Caption and source still show in the expanded view.
   */
  bare?: boolean;
}

/** A sourced reference in a chat response, with the source retained on failure. */
export function ChatReferenceImage({
  imageUrl,
  loading,
  caption,
  altText,
  sourceTitle,
  sourceUrl,
  onSourceClick,
  onRetry,
  onLoadError,
  bare = false,
}: ChatReferenceImageProps) {
  const [expanded, setExpanded] = useState(false);
  const [failedUrl, setFailedUrl] = useState<string>();
  const [loadedUrl, setLoadedUrl] = useState<string>();
  const failed = !!imageUrl && failedUrl === imageUrl;
  const loaded = !!imageUrl && loadedUrl === imageUrl;

  const unavailable = failed || (!loading && !imageUrl);
  const source = sourceUrl ? (
    <a
      className="indigo-chat-reference-image__source"
      href={sourceUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      Source: {sourceTitle}
    </a>
  ) : onSourceClick ? (
    <button
      type="button"
      className="indigo-chat-reference-image__source indigo-chat-reference-image__source--button"
      onClick={() => {
        setExpanded(false);
        onSourceClick();
      }}
    >
      Source: {sourceTitle}
    </button>
  ) : (
    <span className="indigo-chat-reference-image__source indigo-chat-reference-image__source--text">
      Source: {sourceTitle}
    </span>
  );

  return (
    <figure
      className={
        bare
          ? "indigo-chat-reference-image indigo-chat-reference-image--bare w-full"
          : "indigo-chat-reference-image w-full max-w-sm"
      }
      data-testid="chat-reference-image"
    >
      {!bare && (
        <Body as="span" size="xs">
          Reference image
        </Body>
      )}
      {!unavailable && (loading || !loaded) && (
        <div role="status" aria-label="Loading reference image">
          <LoadingPlaceholder size="sm" noLogo />
        </div>
      )}
      {imageUrl && !failed && (
        <button
          type="button"
          className="indigo-chat-reference-image__preview"
          onClick={() => setExpanded(true)}
          aria-label={`Expand reference image: ${altText}`}
        >
          <img
            src={imageUrl}
            alt={altText}
            referrerPolicy="no-referrer"
            onLoad={() => setLoadedUrl(imageUrl)}
            onError={() => {
              setFailedUrl(imageUrl);
              onLoadError?.();
            }}
          />
        </button>
      )}
      {unavailable && (
        <div className="indigo-chat-reference-image__error" role="status">
          <Body size="sm">
            {sourceUrl || onSourceClick
              ? "Image unavailable. You can still open the source."
              : "Image unavailable."}
          </Body>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFailedUrl(undefined);
              setLoadedUrl(undefined);
              onRetry();
            }}
          >
            Retry image
          </Button>
        </div>
      )}
      {!bare && (
        <figcaption>
          <Body size="sm">{caption}</Body>
          <Body size="xs">{source}</Body>
        </figcaption>
      )}
      <Modal
        open={expanded}
        onCancel={() => setExpanded(false)}
        centered
        className="indigo-chat-reference-image-modal"
        footer={
          <div className="indigo-chat-reference-image-modal__caption">
            <Body size="sm">{caption}</Body>
            <Body size="xs">{source}</Body>
          </div>
        }
        title={
          <Body as="span" size="md" className="font-semibold">
            Reference image
          </Body>
        }
        width="min(56rem, calc(100vw - 2 * var(--indigo-spacing-md)))"
      >
        <div className="indigo-chat-reference-image-modal__stage">
          <img
            className="indigo-chat-reference-image__expanded"
            src={imageUrl}
            alt={altText}
            referrerPolicy="no-referrer"
          />
        </div>
      </Modal>
    </figure>
  );
}
